// src/components/HeatsCarousel.jsx
import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Pencil, Crosshair, Target, AlertTriangle, ChevronLeft, ChevronRight, Clock3, Lock as LockIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import HelmetIcon from "@/components/HelmetIcon";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import TeamLogo from "@/components/TeamLogo";
import { useAccountPreferences } from "@/queries/account";

import { computeHeatBonuses } from "@/lib/computeHeatBonuses";
import { formatHeatTimeDisplay } from "@/lib/heatTime";
import { isGuestRiderForTeam } from "@/lib/guestRiders";
import { getNativePlatform } from "@/lib/runtime";
import { getHelmetAccentStyle } from "@/lib/helmetAccent";


// TRUE för fri scroll framåt, FALSE för 1 heat i taget som är avklarat.
const IGNORE_MAX = true; // <-- sätt false för att återgå till gamla beteendet

function getStatusBadge(status, className) {
    switch (status) {
        case "confirmed": return <Badge className={className}>Bekräftad</Badge>;
        case "live": return <Badge className={className} variant="destructive">Pågår</Badge>;
        case "completed": return <Badge className={className} variant="secondary">Avslutad</Badge>;
        case "upcoming":
        default: return <Badge className={className} variant="secondary">Kommande</Badge>;
    }
}

// function getPositionColor(position) {
//     switch (position) {
//         case 1: return "bg-yellow-500 text-black";
//         case 2: return "bg-gray-400 text-white";
//         case 3: return "bg-amber-600 text-white";
//         case 4: return "bg-gray-600 text-white";
//         default: return "bg-gray-200 text-gray-700";
//     }
// }

const defaultIsHeatSavedComplete = (heat) => {
    if (!heat || !Array.isArray(heat.results) || heat.results.length === 0) return false;
    const used = new Set();
    for (const gate of Object.keys(heat.riders || {}).sort()) {
        const riderId = heat.riders[gate]?.rider_id;
        const res = heat.results.find((r) => String(r.rider_id) === String(riderId));
        if (!res) return false;
        if (res.status === "completed") {
            const pos = Number(res.position);
            if (!Number.isInteger(pos) || pos < 1 || pos > 4) return false;
            if (used.has(pos)) return false;
            used.add(pos);
        } else if (!res.status) {
            return false;
        }
    }
    return true;
};


const getTeamShortLabel = (name, fallback) => {
    if (typeof name === "string" && name.trim().length > 0) {
        const trimmed = name.trim();
        const [firstWord] = trimmed.split(/\s+/);
        if (!firstWord) return trimmed.slice(0, 3) || fallback || "";
        return firstWord.slice(0, Math.max(1, Math.min(3, firstWord.length)));
    }
    return fallback ?? "";
};

const isPlaceholderRiderCard = (rider, homeTeamName, awayTeamName) => {
    const riderName = String(rider?.name || "").trim().toLowerCase();
    const homeName = String(homeTeamName || "").trim().toLowerCase();
    const awayName = String(awayTeamName || "").trim().toLowerCase();
    return (
        !rider?.rider_id ||
        !riderName ||
        riderName === homeName ||
        riderName === awayName
    );
};


// Carousel för heat som nu även kan bubbla upp nominering av heat 15 via props
export default function HeatsCarousel({
    match,
    onEditHeat,
    isHeatComplete = defaultIsHeatSavedComplete,
    onIndexChange,
    onSlideChange,
    onNominateHeat,
    canNominateHeat15 = false,
    focusTarget = null,
    headerActions = null,
    embedded = false,
}) {
    const isNativeAndroid = getNativePlatform() === "android";
    const { data: preferenceData } = useAccountPreferences();
    const heats = Array.isArray(match?.heats)
        ? [...match.heats].sort((a, b) => a.heat_number - b.heat_number)
        : [];
    const homeTeamName = match?.home_team || "Hemmalag";
    const awayTeamName = match?.away_team || "Bortalag";
    const riderContextById = React.useMemo(() => {
        const map = new Map();
        const collect = (entries = [], teamKey = "") => {
            const teamId = teamKey === "home" ? match?.home_team_id : match?.away_team_id;
            entries.forEach((entry) => {
                const riderId = String(entry?.rider_id || entry?.id || "");
                if (!riderId) return;
                const existing = map.get(riderId) || {};
                map.set(riderId, {
                    ...existing,
                    ...entry,
                    rider_id: riderId,
                    id: riderId,
                    context_team_id: entry?.context_team_id || existing.context_team_id || teamId || "",
                });
            });
        };

        ["lineups", "initial_lineups", "source_lineups"].forEach((key) => {
            const lineups = match?.[key] || {};
            collect(lineups?.home || [], "home");
            collect(lineups?.away || [], "away");
        });

        return map;
    }, [match]);

    // Senast ifyllt heat för att veta var vi ska landa i karusellen
    const latestFilledIdx = Math.max(-1, heats.reduce((acc, h, i) => (isHeatComplete(h) ? i : acc), -1));
    // Första heat som inte är klart (används som logiskt start-index)
    const startHeatIndex = Math.max(0, Math.min(heats.length - 1, latestFilledIdx + 1));

    // Mobil-detektion
    const [isMobile, setIsMobile] = React.useState(() => typeof window !== "undefined" ? window.innerWidth < 768 : true);
    React.useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);



    const initializedRef = React.useRef(false);
    const lastWarnRef = React.useRef(0);

    const [pointsMode, setPointsMode] = React.useState("heat");
    const showTotals = pointsMode === "total";

    const homeTeamShort = React.useMemo(() => {
        return (
            match?.home_team_short ||
            match?.home_team_abbreviation ||
            getTeamShortLabel(match?.home_team, "Hem")
        );
    }, [match?.home_team, match?.home_team_abbreviation, match?.home_team_short]);

    const awayTeamShort = React.useMemo(() => {
        return (
            match?.away_team_short ||
            match?.away_team_abbreviation ||
            getTeamShortLabel(match?.away_team, "Bor")
        );
    }, [match?.away_team, match?.away_team_abbreviation, match?.away_team_short]);

    const { totalsByHeat, heatScores } = React.useMemo(() => {
        const totals = {};
        const perHeat = {};
        const perHeatScore = {};

        heats.forEach((heat) => {
            if (!heat) return;
            const riderMap = {};
            Object.keys(heat.riders || {}).forEach((gate) => {
                const rider = heat.riders[gate];
                if (!rider || rider.rider_id == null) return;
                riderMap[String(rider.rider_id)] = rider;
            });

            const bonuses = computeHeatBonuses(heat) || {};
            let homePoints = 0;
            let awayPoints = 0;

            (heat.results || []).forEach((result) => {
                if (!result) return;
                const riderId = String(result.rider_id ?? "");
                if (!riderId || !riderMap[riderId]) return;
                if (result.status === "completed") {
                    const basePoints = Number(result.points || 0);
                    const bonusPoints = Number(bonuses?.[riderId] || 0);
                    const currentTotals = totals[riderId] || { base: 0, bonus: 0 };
                    totals[riderId] = {
                        base: currentTotals.base + basePoints,
                        bonus: currentTotals.bonus + bonusPoints,
                    };
                    if (riderMap[riderId].team === "home") {
                        homePoints += basePoints;
                    } else if (riderMap[riderId].team === "away") {
                        awayPoints += basePoints;
                    }
                }
            });

            if (heat.status === "completed") {
                perHeatScore[heat.heat_number] = { home: homePoints, away: awayPoints };
            }

            const totalsForHeat = {};
            Object.values(riderMap).forEach((rider) => {
                const riderId = String(rider.rider_id ?? "");
                if (!riderId) return;
                const snapshot = totals[riderId] || { base: 0, bonus: 0 };
                totalsForHeat[riderId] = { ...snapshot };
            });
            perHeat[heat.heat_number] = totalsForHeat;
        });

        return { totalsByHeat: perHeat, heatScores: perHeatScore };
    }, [heats]);


    // Hitta index för finalheatet (15) och se om vi behöver ett separat nomineringskort
    const nominationHeatIndex = React.useMemo(
        () => heats.findIndex((heat) => Number(heat?.heat_number) === 15),
        [heats],
    );

    // Praktisk referens till heat 15 för att kunna visa summering och låsa upp registrering
    const heat15 = nominationHeatIndex >= 0 ? heats[nominationHeatIndex] : null;

    // Är alla fyra gateplatserna bemannade? Om ja kan heatet registreras som vanligt
    const hasNominationAssignments = React.useMemo(() => {
        if (!heat15) return false;
        const riders = Object.values(heat15.riders || {});
        return riders.filter((slot) => slot && slot.rider_id != null).length >= 4;
    }, [heat15]);

    // Vi visar nomineringskortet endast när vi har heat 15 och någon kan öppna dialogen
    const showNominationSlide = Boolean(onNominateHeat) && nominationHeatIndex >= 0;

    // Hjälp för att konvertera heat-index -> slide-index när vi injicerar nomineringsrutan
    const heatIndexToSlideIndex = React.useCallback(
        (heatIdx) => {
            if (!showNominationSlide || nominationHeatIndex < 0) return heatIdx;
            return heatIdx + (heatIdx >= nominationHeatIndex ? 1 : 0);
        },
        [showNominationSlide, nominationHeatIndex],
    );

    // Antalet slides i karusellen (heats + eventuell nomineringsruta)
    const totalSlides = heats.length + (showNominationSlide ? 1 : 0);

    // Bestäm vilket slide-index som ska vara startpunkt.
    // Om vi precis har sparat heat 14 vill vi landa på nomineringskortet innan heat 15.
    const startSlideIndex = React.useMemo(() => {
        if (
            showNominationSlide &&
            nominationHeatIndex >= 0 &&
            startHeatIndex >= nominationHeatIndex &&
            !hasNominationAssignments
        ) {
            return nominationHeatIndex;
        }
        return heatIndexToSlideIndex(startHeatIndex);
    }, [
        hasNominationAssignments,
        heatIndexToSlideIndex,
        nominationHeatIndex,
        showNominationSlide,
        startHeatIndex,
    ]);
    
    const apiRef = React.useRef(null);
    const lastHandledFocusRequestRef = React.useRef(null);
    const [index, setIndex] = React.useState(startSlideIndex);
    const hasInitializedScrollRef = React.useRef(false);
    const autoScrollNextHeatEnabled =
        preferenceData?.preferences?.auto_scroll_next_heat !== false;

    // Max tillåten slide (du får alltid gå bakåt; framåt bara till första ouppfyllda heatet)
    const maxAllowedHeatIndex = IGNORE_MAX
        ? Math.max(0, heats.length - 1) // navigera fritt fram till sista heat
        : Math.max(0, Math.min(heats.length - 1, latestFilledIdx + 1));
    const maxAllowedIndex = Math.max(0, Math.min(totalSlides - 1, heatIndexToSlideIndex(maxAllowedHeatIndex)));
    const maxAllowedRef = React.useRef(maxAllowedIndex);
    React.useEffect(() => { maxAllowedRef.current = maxAllowedIndex; }, [maxAllowedIndex]);

    // När matchdatan uppdateras hoppar vi till rätt slide för första ouppfyllda heatet
    React.useEffect(() => {
        const shouldScroll =
            !hasInitializedScrollRef.current || autoScrollNextHeatEnabled;
        if (shouldScroll && apiRef.current) {
            try { apiRef.current.scrollTo(startSlideIndex); } catch { }
            setIndex(startSlideIndex);
        } else if (shouldScroll) {
            setIndex(startSlideIndex);
        }
        hasInitializedScrollRef.current = true;
    }, [autoScrollNextHeatEnabled, startSlideIndex]);

    // Inversen ovan används för att översätta tillbaka till heat-index vid hintar/toasts
    const slideIndexToHeatIndex = React.useCallback(
        (slideIdx) => {
            if (!showNominationSlide || nominationHeatIndex < 0) return slideIdx;
            if (slideIdx === nominationHeatIndex) {
                // Nomineringskortet representerar heat 15 logiskt
                return nominationHeatIndex;
            }
            return slideIdx - (slideIdx > nominationHeatIndex ? 1 : 0);
        },
        [showNominationSlide, nominationHeatIndex],
    );

    const slideIndexToMeta = React.useCallback(
        (slideIdx) => {
            const heatIdx = slideIndexToHeatIndex(slideIdx);
            const heat = heats[heatIdx] || null;
            const isNominationSlide =
                Boolean(showNominationSlide) &&
                nominationHeatIndex >= 0 &&
                slideIdx === nominationHeatIndex;

            return {
                type: isNominationSlide ? "nomination" : "heat",
                heatIndex: heatIdx,
                heatNumber: Number(heat?.heat_number || 0),
            };
        },
        [heats, nominationHeatIndex, showNominationSlide, slideIndexToHeatIndex],
    );



    // // ... överst i komponenten, efter heats/startIndex mm: KOD TILLHÖRANDE TEST MED NOMINERINGSKNAPP I CAROUSEL

    // // Hur många heats är kompletta?
    // const completedHeats = heats.filter(h => isHeatComplete(h)).length;

    // // Ska vi visa nominations-sliden?
    // const insertionIndex = 13 - 1; // efter Heat 13 (0-baserat: 12)
    // const showNominationSlide = completedHeats >= 13;

    // // Hjälpfunktioner: mappa heatIndex <-> slideIndex när en extra slide injekteras
    // const heatIndexToSlideIndex = React.useCallback((hIdx) => {
    //     if (!showNominationSlide) return hIdx;
    //     return hIdx + (hIdx >= insertionIndex + 1 ? 1 : 0);
    // }, [showNominationSlide]);

    // const slideIndexToHeatIndex = React.useCallback((sIdx) => {
    //     if (!showNominationSlide) return sIdx;
    //     // sIdx === insertionIndex+1 => nominations-sliden (ingen heat)
    //     if (sIdx === insertionIndex + 1) return insertionIndex + 1; // mappar till första ouppfyllda (14)
    //     return sIdx - (sIdx > insertionIndex + 1 ? 1 : 0);
    // }, [showNominationSlide]);

    // // Hur många slides har vi totalt (inkl. ev. nomination)?
    // const totalSlides = heats.length + (showNominationSlide ? 1 : 0);














    const setApi = React.useCallback((api) => {
        apiRef.current = api;
        if (!api) return;
        if (!initializedRef.current) {
            initializedRef.current = true;
            try { api.scrollTo(startSlideIndex, true); } catch { }
        }
        const onSelect = () => {
            const i = api.selectedScrollSnap();
            // Låsning: på mobil tillåter vi inte att gå förbi maxAllowedIndex
            if (isMobile && i > maxAllowedRef.current) {
                // Hoppa tillbaka
                api.scrollTo(maxAllowedRef.current);
                // Throttlad hint
                const now = Date.now();
                if (now - lastWarnRef.current > 1200) {
                    lastWarnRef.current = now;
                    const hint = showNominationSlide && maxAllowedRef.current === nominationHeatIndex
                        ? "Nominera heat 15 först"
                        : `Fyll i Heat ${Math.min(heats.length, slideIndexToHeatIndex(maxAllowedRef.current) + 1)} först`;
                    toast.info(hint);
                }
                return;
            }
            setIndex(i);
            onIndexChange?.(slideIndexToHeatIndex(i));
            onSlideChange?.(slideIndexToMeta(i));
        };
        onSelect();
        api.on("select", onSelect);
    }, [heats.length, isMobile, nominationHeatIndex, onIndexChange, onSlideChange, showNominationSlide, slideIndexToHeatIndex, slideIndexToMeta, startSlideIndex]);

    // Embla options (memoiserade)
    const emblaOpts = React.useMemo(() => ({
        align: "start",
        containScroll: "keepSnaps",
        dragFree: false,
        loop: false,
        slidesToScroll: 1,
    }), []);

    // const goToLatest = () => {
    //     if (!apiRef.current) return;
    //     apiRef.current.scrollTo(Math.max(0, latestFilledIdx));
    // };

    React.useEffect(() => {
        if (!apiRef.current || !focusTarget?.requestKey) return;
        if (lastHandledFocusRequestRef.current === focusTarget.requestKey) return;

        lastHandledFocusRequestRef.current = focusTarget.requestKey;

        if (focusTarget.type === "nomination" && showNominationSlide && nominationHeatIndex >= 0) {
            apiRef.current.scrollTo(nominationHeatIndex);
            return;
        }

        if (focusTarget.type === "heat" && typeof focusTarget.heatNumber === "number") {
            const targetHeatIndex = heats.findIndex(
                (heat) => Number(heat?.heat_number) === Number(focusTarget.heatNumber)
            );
            if (targetHeatIndex >= 0) {
                apiRef.current.scrollTo(heatIndexToSlideIndex(targetHeatIndex));
            }
        }
    }, [focusTarget, heatIndexToSlideIndex, heats, nominationHeatIndex, showNominationSlide]);

    const canGoPrev = index > 0;
    const canGoNext = index < totalSlides - 1 && (!isMobile || index < maxAllowedIndex);

    const handlePrev = () => {
        if (!apiRef.current || !canGoPrev) return;
        apiRef.current.scrollTo(index - 1);
    };
    const handleNext = () => {
        if (!apiRef.current || !canGoNext) {
            if (isMobile && index >= maxAllowedIndex) {
                const hint = showNominationSlide && maxAllowedRef.current === nominationHeatIndex
                    ? "Nominera heat 15 först"
                    : `Fyll i Heat ${Math.min(heats.length, slideIndexToHeatIndex(maxAllowedRef.current) + 1)} först`;
                toast.info(hint);
            }
            return;
        }
        apiRef.current.scrollTo(index + 1);
    };


    const accentForCard = (rider, gate) => {
        if (rider?.team === "home") {
            return (gate === "1" || gate === "3")
                ? { ring: "ring-red-300/60 dark:ring-red-700/60", bar: "from-red-500/70", glow: "shadow-[0_8px_30px_rgba(239,68,68,0.10)]" }
                : { ring: "ring-blue-300/60 dark:ring-blue-700/60", bar: "from-blue-500/70", glow: "shadow-[0_8px_30px_rgba(59,130,246,0.10)]" };
        }
        return (gate === "2" || gate === "4")
            ? { ring: "ring-amber-300/60 dark:ring-amber-700/60", bar: "from-amber-500/70", glow: "shadow-[0_8px_30px_rgba(245,158,11,0.10)]" }
            : { ring: "ring-zinc-300/60 dark:ring-zinc-700/60", bar: "from-zinc-500/60", glow: "shadow-[0_8px_30px_rgba(113,113,122,0.10)]" };
    };

    const slides = React.useMemo(() => {
        if (!showNominationSlide || nominationHeatIndex < 0) {
            return heats.map((heat) => ({ type: "heat", heat, key: `heat-${heat.heat_number}` }));
        }
        const list = [];
        heats.forEach((heat, heatIdx) => {
            if (heatIdx === nominationHeatIndex) {
                list.push({ type: "nomination", key: "nomination-heat-15" });
            }
            list.push({ type: "heat", heat, key: `heat-${heat.heat_number}` });
        });
        return list;
    }, [heats, nominationHeatIndex, showNominationSlide]);

    const positionBadgeClass = (pos) => {
        switch (Number(pos)) {
            case 1: return "bg-emerald-600 text-white";
            case 2: return "bg-sky-600 text-white";
            case 3: return "bg-amber-600 text-white";
            case 4: return "bg-slate-600 text-white";
            default: return "bg-secondary text-secondary-foreground";
        }
    };

    const pointsToggle = (
        <ToggleGroup
            id="riderPointsToggle"
            type="single"
            value={pointsMode}
            onValueChange={(val) => val && setPointsMode(val)}
            size="sm"
            aria-label="Växla poängvy"
            className={cn(
                "inline-flex h-8 items-center gap-1 rounded-full border border-border bg-background p-1 shadow-inner",
                isNativeAndroid && "native-v3-points-toggle"
            )}
        >
            <ToggleGroupItem
                value="heat"
                aria-label="Visa heatpoäng"
                className={cn(
                    "h-6 rounded-full px-2.5 text-[11px] font-semibold transition-all",
                    "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm",
                    "data-[state=off]:text-muted-foreground"
                )}
            >
                Heatpoäng
            </ToggleGroupItem>
            <ToggleGroupItem
                value="total"
                aria-label="Visa totalpoäng"
                className={cn(
                    "h-6 rounded-full px-2.5 text-[11px] font-semibold transition-all",
                    "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm",
                    "data-[state=off]:text-muted-foreground"
                )}
            >
                Totalpoäng
            </ToggleGroupItem>
        </ToggleGroup>
    );

    const carouselBody = (
        <>
            <CardContent className={cn("px-2 pb-3", embedded && "px-0 pb-0", isNativeAndroid && "native-v3-heat-carousel-content")}>
                <Carousel className={cn("w-full", isNativeAndroid && "native-v3-heat-carousel")} setApi={setApi} opts={emblaOpts}>
                    <CarouselContent>
                        {slides.map((slide, slideIdx) => {
                            // När vi passerar mellan heat 14 och 15 visar vi en separat nomineringsruta
                            if (slide.type === "nomination") {
                                const hasSummary = hasNominationAssignments && Boolean(heat15);

                                return (
                                    <CarouselItem key={slide.key} className="basis-full md:basis-1/2 lg:basis-1/3">
                                        <Card className={cn("flex h-full flex-col border border-dashed border-primary/40 bg-card/80", isNativeAndroid && "native-v3-heat-nomination-card")}>
                                            <CardHeader className="native-v3-heat-nomination-header px-4 py-4">
                                                {isNativeAndroid ? (
                                                    <span className="native-v3-heat-nomination-icon" aria-hidden="true">
                                                        <Target />
                                                    </span>
                                                ) : null}
                                                <div>
                                                    {isNativeAndroid ? <span className="native-v3-heat-nomination-kicker">FINALHEAT</span> : null}
                                                    <CardTitle className="text-base">Nominering Heat 15</CardTitle>
                                                    <CardDescription>
                                                        Sätt startbanor och förare innan heat 15.
                                                    </CardDescription>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="native-v3-heat-nomination-content flex-1 space-y-3 px-4 pb-2 text-sm">
                                                {hasSummary ? (
                                                    <div className="native-v3-heat-nomination-summary space-y-2">
                                                        <p className="native-v3-heat-nomination-summary-label text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                            Aktuell nominering
                                                        </p>
                                                        <div className="native-v3-heat-nomination-grid grid grid-cols-2 gap-2">
                                                            {Object.keys(heat15?.riders || {})
                                                                .sort((a, b) => Number(a) - Number(b))
                                                                .map((gate) => {
                                                                    const rider = heat15?.riders?.[gate];
                                                                    return (
                                                                        <div
                                                                            key={gate}
                                                                            className="native-v3-heat-nomination-rider rounded-lg border border-border/60 bg-muted/40 p-3"
                                                                            style={isNativeAndroid ? getHelmetAccentStyle(rider?.helmet_color) : undefined}
                                                                        >
                                                                            <p className="native-v3-heat-nomination-gate text-[11px] text-muted-foreground">
                                                                                Bana {gate}
                                                                            </p>
                                                                            <p className="native-v3-heat-nomination-name text-sm font-semibold">
                                                                                {rider?.name || "Ej vald"}
                                                                            </p>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                        <p className="native-v3-heat-nomination-hint text-xs text-muted-foreground">
                                                            Klicka på knappen för att uppdatera nomineringen vid behov.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="native-v3-heat-nomination-empty rounded-lg border border-dashed border-muted-foreground/50 bg-muted/20 px-3 py-4 text-muted-foreground">
                                                        <Target aria-hidden="true" />
                                                        <span>
                                                            <strong>Finalheatet förbereds</strong>
                                                            När heat 14 är registrerat kan du nominera förare till heat 15.
                                                        </span>
                                                    </div>
                                                )}
                                            </CardContent>
                                            <CardFooter className="native-v3-heat-nomination-footer flex items-center justify-center p-6 pt-0 mt-auto">
                                                {!canNominateHeat15 ? (
                                                    <div className="native-v3-heat-nomination-locked flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-muted-foreground/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                                                        <LockIcon className="h-4 w-4" />
                                                        Nominering låses upp efter heat 14
                                                    </div>
                                                ) : hasSummary ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="native-v3-heat-nomination-edit w-full"
                                                        onClick={() => onNominateHeat?.()}
                                                    >
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        Redigera nominering
                                                    </Button>
                                                ) : null}
                                            </CardFooter>
                                        </Card>
                                    </CarouselItem>
                                );
                            }

                            const heat = slide.heat;
                            if (!heat) return null;
                            // Beräkna bonus för detta heat i frontend
                            const bonuses = computeHeatBonuses(heat);


                            const heatIndex = slideIndexToHeatIndex(slideIdx);
                            const prevHeat = heatIndex > 0 ? heats[heatIndex - 1] : null;
                            const canOpenThis = heat.heat_number === 1 || (prevHeat && isHeatComplete(prevHeat));
                            const totalsForHeat = totalsByHeat?.[heat.heat_number] || {};
                            const heatScore = heatScores?.[heat.heat_number];
                            const heatScoreLabel = heatScore
                                ? `${homeTeamShort} ${heatScore.home} - ${heatScore.away} ${awayTeamShort}`
                                : null;
                            const heatTimeLabel = formatHeatTimeDisplay(heat?.heat_time || "");
                            const requiresNominationUnlock = heat.heat_number === 15 && showNominationSlide;
                            const nominationReady = !requiresNominationUnlock || hasNominationAssignments;
                            return (
                                <CarouselItem key={slide.key} className="basis-full md:basis-1/2 lg:basis-1/3">
                                    <Card className={cn("border-none shadow-none h-full flex flex-col", isNativeAndroid && "native-v3-heat-slide")}>
                                        <CardHeader className={cn("px-2 py-3", isNativeAndroid && "native-v3-heat-slide-header")}>
                                            {/* <div className="flex items-center justify-between"> */}
                                            <div className="flex items-center justify-between gap-2">
                                                <CardTitle className="text-base">Heat {heat.heat_number}</CardTitle>
                                                {/* <div className="flex items-center gap-2">{getStatusBadge(heat.status)}</div> */}


                                                {heat.status === "completed" && heatScoreLabel ? (
                                                    <Badge variant="outline" className={cn("text-xs font-semibold tracking-tight", isNativeAndroid && "native-v3-heat-score")}>
                                                        <div className="flex flex-col items-start">
                                                            <p>Heatresultat: </p>
                                                            <p className="text-sm">{heatScoreLabel}</p>
                                                        </div>

                                                    </Badge>
                                                ) : null}
                                                {getStatusBadge(heat.status, isNativeAndroid ? "native-v3-heat-status" : undefined)}

                                            </div>

                                        </CardHeader>

                                        <CardContent className={cn("p-1 pb-3 space-y-3", isNativeAndroid && "native-v3-heat-slide-body")}>
                                            {heatTimeLabel ? (
                                                <div className="flex justify-center">
                                                    <span className={cn("inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-sky-500/10 px-2.5 py-1 text-[11px] font-medium text-sky-800 dark:border-sky-400/40 dark:bg-sky-500/15 dark:text-sky-100", isNativeAndroid && "native-v3-heat-time")}>
                                                        <Clock3 className="h-3.5 w-3.5 shrink-0" />
                                                        <span>{heatTimeLabel}</span>
                                                    </span>
                                                </div>
                                            ) : null}
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                {Object.keys(heat.riders || {}).sort().map((gate) => {
                                                    const rider = heat.riders[gate];
                                                    const result = (heat.results || []).find((r) => String(r.rider_id) === String(rider.rider_id));
                                                    const isPlaceholderRider = isPlaceholderRiderCard(rider, homeTeamName, awayTeamName);
                                                    const riderDisplayName = isPlaceholderRider
                                                        ? "Förare ej nominerad"
                                                        : rider.name;

                                                    // Bonus uträknad på klienten
                                                    const bonus = Number(bonuses?.[String(rider.rider_id)] || 0);
                                                    const basePoints = Number(result?.points || 0);


                                                    const accent = accentForCard(rider, gate);
                                                    const riderId = String(rider.rider_id ?? "");
                                                    const totalPointsForRider = totalsForHeat?.[riderId] || { base: 0, bonus: 0 };
                                                    const heatPointsLabel = bonus > 0
                                                        ? `${basePoints}+${bonus}p`
                                                        : `${basePoints}p`;
                                                    const totalPointsLabel = totalPointsForRider.bonus > 0
                                                        ? `Tot ${totalPointsForRider.base}+${totalPointsForRider.bonus}p`
                                                        : `Tot ${totalPointsForRider.base}p`;
                                                    const pointsLabel = showTotals && heat.status === "completed"
                                                        ? totalPointsLabel
                                                        : `(${heatPointsLabel})`;

                                                    return (

                                                        <Card
                                                            key={gate}
                                                            role="group"
                                                            style={isNativeAndroid ? getHelmetAccentStyle(rider.helmet_color) : undefined}
                                                            className={cn(
                                                                // bas: modern card
                                                                "relative overflow-hidden rounded-2xl border border-border/50",
                                                                "bg-gradient-to-b from-background/80 to-background/60",
                                                                "backdrop-blur-sm ring-1 ring-inset",
                                                                "transition-all duration-200 ease-out",
                                                                "hover:border-border hover:shadow-md hover:translate-y-[-1px]",
                                                                accent.ring, accent.glow,
                                                                "p-3",
                                                                isNativeAndroid && "native-v3-heat-rider"
                                                            )}
                                                        >
                                                            {/* dekorativ toppbar m. mjuk gradient */}
                                                            <span className={cn(
                                                                "native-v3-heat-rider-accent pointer-events-none absolute inset-x-0 top-0 h-1.5",
                                                                "bg-gradient-to-r", accent.bar, "via-transparent to-transparent opacity-80"
                                                            )} />

                                                            {!isPlaceholderRider ? (
                                                                <TeamLogo
                                                                    teamName={rider.team === "home" ? homeTeamName : awayTeamName}
                                                                    size={16}
                                                                    className="native-v3-heat-rider-watermark h-4 w-4 shrink-0 object-contain"
                                                                />
                                                            ) : null}

                                                            {/* head: gate + hjälm */}
                                                            <div className={cn("flex items-center justify-between", isNativeAndroid && "native-v3-heat-rider-top")}>
                                                                <div className="flex items-center gap-2">
                                                                    <span
                                                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-card pl-[1px] text-[11px] font-medium text-foreground ring-1 ring-foreground/30"
                                                                        aria-label={`Bana ${gate}`}
                                                                    >
                                                                        {`${gate}.`}
                                                                    </span>

                                                                    <div className="flex items-center gap-1">
                                                                        <HelmetIcon
                                                                            color={rider.helmet_color}
                                                                            className="h-4 w-4"
                                                                            title={`Hjälm: ${rider.helmet_color}`}
                                                                            decorative={false}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="ml-auto flex items-center gap-2">
                                                                    <span className="opacity-0 group-hover:opacity-100 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground transition">
                                                                        kort
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* namn */}
                                                            <div className={cn("mt-2 flex items-center gap-2 truncate text-sm font-semibold tracking-tight", isNativeAndroid && "native-v3-heat-rider-name")}>
                                                                <span className={cn("truncate", isPlaceholderRider && "text-muted-foreground")}>{riderDisplayName}</span>
                                                                {!isPlaceholderRider && isGuestRiderForTeam(riderContextById.get(String(rider.rider_id || "")), riderContextById.get(String(rider.rider_id || ""))?.context_team_id) ? (
                                                                    <GuestRiderBadge iconOnly className="shrink-0 px-1" />
                                                                ) : null}
                                                            </div>

                                                            {/* meta: resultat / status */}
                                                            {result && (
                                                                <div className={cn("mt-2 flex items-center justify-center", isNativeAndroid && "native-v3-heat-rider-result")}>
                                                                    {result.status === "completed" ? (
                                                                        <span className={cn("inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[11px]", positionBadgeClass(result.position))}>
                                                                            <span className="font-semibold">{result.position}.
                                                                            </span>
                                                                            <span className="opacity-90">{pointsLabel}</span>
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[11px] bg-destructive text-destructive-foreground">
                                                                            <AlertTriangle className="w-3 h-3" />
                                                                            {String(result.status || "X").toUpperCase() === "EXCLUDED"
                                                                                ? "X"
                                                                                : String(result.status || "X").toUpperCase()}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Card>
                                                    );
                                                })}
                                            </div>
                                        </CardContent>

                                        <CardFooter className="justify-center mt-auto">
                                            <TooltipProvider>
                                                {(() => {
                                                    if (!canOpenThis) {
                                                        return (
                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                <LockIcon className="h-4 w-4" />
                                                                Fyll i Heat {Math.max(1, heat.heat_number - 1)} först
                                                            </div>
                                                        );
                                                    }

                                                    if (requiresNominationUnlock && !nominationReady) {
                                                        return (
                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                <LockIcon className="h-4 w-4" />
                                                                Nominera heat 15 för att låsa upp registrering
                                                            </div>
                                                        );
                                                    }

                                                    if (heat.status === "completed") {
                                                        return (
                                                            <Button
                                                                id="heatDialogLauncher"
                                                                className={cn("w-fit", isNativeAndroid && "native-v3-heat-edit")}
                                                                onClick={() => onEditHeat?.(heat)}
                                                                size="sm"
                                                                aria-label="Ändra heat"
                                                            >
                                                                <Pencil className="h-4 w-4 mr-2" />
                                                                Ändra heat
                                                            </Button>
                                                        );
                                                    }

                                                    return (
                                                        null
                                                    );
                                                })()}
                                            </TooltipProvider>
                                        </CardFooter>
                                    </Card>
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>

                    {/* Egna navigationer (så vi kan disabla Next vid låsning) */}
                    <div className={cn("border-t border-muted-foreground px-2 pb-2 pt-2", isNativeAndroid && "native-v3-heat-navigation")}>
                        <div className="flex items-center justify-between">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handlePrev}
                                disabled={!canGoPrev}
                                aria-label="Föregående heat"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>

                            <div className="text-xs text-muted-foreground">
                                {totalSlides ? `${index + 1}/${totalSlides}` : "0/0"}
                            </div>

                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleNext}
                                disabled={!canGoNext}
                                aria-label="Nästa heat"
                                className={cn(!canGoNext && "opacity-60")}
                            >
                                {isMobile && index >= maxAllowedIndex ? <LockIcon className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                            </Button>
                        </div>
                        {embedded ? (
                            <div className="mt-2 flex justify-center">
                                {pointsToggle}
                            </div>
                        ) : null}
                    </div>
                </Carousel>
            </CardContent>
        </>
    );

    if (embedded) {
        return carouselBody;
    }

    return (
        <Card className="rounded-md border bg-card">
            <CardHeader className="p-4 border-b border-muted-foreground">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <CardTitle>Heat Program</CardTitle>
                        <CardDescription>Bläddra mellan heat 1–15</CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        {headerActions}
                        {pointsToggle}
                    </div>
                </div>
            </CardHeader>

            {carouselBody}
        </Card>
    );
}


